import * as React from 'react'
import slugify from 'slugify'

import { useTenant } from '@rewardops/auth'
import { cn, LocalizedLink, useLocale } from '@rewardops/ui'

import { filterShopCategories } from '~/utils/category-utils'
import { BrandedCategoryLinks } from '~/components/branded-category-links/branded-category-links'
import { styling } from '~/components/styling'

interface LocaleString {
  [locale: string]: string
}

interface Category {
  mainCategory: {
    id: string
    name: LocaleString
    viewAll?: boolean
    subCategories?: Array<{
      id: string
      code: string
      name: LocaleString
    }>
  }
}

interface CategoryGridProps {
  categories: Category[]
  setIsDropdownOpen: (isOpen: boolean) => void
}

export const CategoryGrid: React.FC<CategoryGridProps> = ({
  categories,
  setIsDropdownOpen,
}) => {
  const { locale } = useLocale()
  const tenant = useTenant()

  if (!categories) return null

  // Safely get localized name with fallback
  const getLocalizedName = (name: LocaleString): string => {
    if (!locale || !name[locale]) {
      return name.default || Object.values(name)[0] || ''
    }
    return name[locale]
  }

  // Safe slugify with fallback
  const safeSlugify = (text: string): string => {
    try {
      return slugify(text.toLowerCase())
    } catch {
      return ''
    }
  }

  let sortedCategories = [...categories]

  // filter out categories with no subcategories
  sortedCategories = sortedCategories.filter((c) =>
    filterShopCategories(c, tenant?.hiddenCategories),
  )

  const getRowSpan = (category: CategoryGridProps['categories'][0]): number => {
    const childCount = category.mainCategory.subCategories?.length || 0
    if (childCount > 10) return 3
    if (childCount > 5) return 2
    return 1
  }

  const getMainCategoryPath = (name: string): string => {
    return safeSlugify(name)
  }

  return (
    <div className="container mx-auto flex px-4 py-4">
      <div className="flex flex-row flex-wrap gap-8">
        {sortedCategories.map((category) => {
          const mainCategoryName = getLocalizedName(category.mainCategory.name)
          const mainCategorySlug = getMainCategoryPath(mainCategoryName)

          return (
            <div
              key={category.mainCategory.id}
              style={{
                gridRow: `span ${getRowSpan(category)}`,
              }}
              className="basis-1/6"
            >
              <LocalizedLink
                id={`main-category-${safeSlugify(mainCategoryName)}`}
                href={`/category/${
                  category?.mainCategory?.viewAll ? 'all' : mainCategorySlug
                }`}
                onClick={() => setIsDropdownOpen(false)}
                className={cn(
                  'mb-2 inline-block text-lg font-bold text-gray-800',
                  styling.Link,
                )}
              >
                {mainCategoryName}
              </LocalizedLink>
              {category.mainCategory.subCategories &&
                category.mainCategory.subCategories.length > 0 && (
                  <ul className="space-y-1">
                    {category.mainCategory.subCategories.map((subCategory) => {
                      const subCategoryName = getLocalizedName(subCategory.name)

                      return (
                        <li key={subCategory.id}>
                          <LocalizedLink
                            key={subCategory.code}
                            id={`sub-category-${safeSlugify(subCategoryName)}`}
                            href={`/category/${mainCategorySlug}?subCategory=${subCategory.code}`}
                            onClick={() => setIsDropdownOpen(false)}
                            className={cn('inline-block text-sm', styling.Link)}
                          >
                            {subCategoryName}
                          </LocalizedLink>
                        </li>
                      )
                    })}
                  </ul>
                )}
            </div>
          )
        })}
      </div>
      <BrandedCategoryLinks />
    </div>
  )
}
